/**
 * 驻场场所模式 · 侧栏菜单裁剪
 * 对应 docs/platform/resident-venue-mode-spec.md §4.2
 * 仅 eventHost | resident
 */
import type { CrmebMenuNode } from '@/utils/crmebMenu';
import { normalizeAppPath } from '@/utils/crmebMenu';

export interface MerchantVenueModeFeatures {
  showCalendar: boolean;
  eventProduct: boolean;
  productionHub: boolean;
  placeRentalSlot: boolean;
}

export type VenueModeValue = 'eventHost' | 'resident';

/** 与后端 MerchantVenueModeService.computeFeatures 一致 */
export function computeVenueFeatures(venueMode?: string): MerchantVenueModeFeatures {
  if (venueMode === 'resident') {
    return {
      showCalendar: true,
      eventProduct: true,
      productionHub: false,
      placeRentalSlot: true,
    };
  }
  return {
    showCalendar: false,
    eventProduct: true,
    productionHub: true,
    placeRentalSlot: true,
  };
}

export function normalizeVenueMode(mode?: string): VenueModeValue {
  if (mode === 'resident') return 'resident';
  return 'eventHost';
}

function menuPathKey(path?: string): string {
  if (!path) return '';
  const normalized = normalizeAppPath(path.startsWith('/') ? path : `/${path}`);
  return normalized.replace(/^\/merchant/, '') || normalized;
}

function shouldHideByFeatures(path: string, features: MerchantVenueModeFeatures): boolean {
  const key = menuPathKey(path);
  if (!key) return false;
  // 活动主办：侧栏不放节目排期 / 票价模板。路由仍在，活动列表可进日历再开启
  if (!features.showCalendar) {
    if (key.includes('show-calendar') || key.includes('show-series')) {
      return true;
    }
  } else if (key.includes('show-series')) {
    // 节目排期：票价模板从日历进入，不占侧栏主入口
    return true;
  }
  // productionHub=false（节目排期）：快捷发活动不是日常入口；专场仍走活动列表
  if (!features.productionHub && key.includes('quickActivity')) {
    return true;
  }
  if (!features.placeRentalSlot) {
    if (key.includes('rental-slot') || key.includes('rental-market') || key.includes('/place/slot')) {
      return true;
    }
  }
  return false;
}

/** 按 venueMode features 裁剪 EB 菜单树（保留有子节点的父级） */
export function filterMenuByVenueFeatures(
  menuList: CrmebMenuNode[],
  features: MerchantVenueModeFeatures,
): CrmebMenuNode[] {
  const result: CrmebMenuNode[] = [];
  (menuList || []).forEach((menu) => {
    let children: CrmebMenuNode[] | undefined;
    if (menu.children?.length) {
      children = filterMenuByVenueFeatures(menu.children, features);
    }
    const hiddenSelf = shouldHideByFeatures(menu.path, features);
    if (hiddenSelf) {
      return;
    }
    if (children?.length) {
      result.push({ ...menu, children });
    } else if (!menu.children?.length) {
      result.push({ ...menu });
    }
  });
  return result;
}
